<template>
    <f7-view main>
        <f7-page>
            <cm-navbar :title="$ml.get('finishedReceipts')" />
            <f7-block v-if="!canCreate">{{ $ml.get('finishedReceiptNoPermission') }}</f7-block>
            <template v-else>
                <f7-block-title>{{ $ml.get('basicInfo') }}</f7-block-title>
                <f7-block v-if="loading">{{ $ml.get('processing') }}</f7-block>
                <f7-list v-else :key="formVersion" inline-labels no-hairlines-md>
                    <f7-list-item :title="$ml.get('finishedReceiptProduct')" smart-select :smart-select-params="selectParams" :disabled="saving">
                        <select v-model="form.material_id" name="material_id">
                            <option value="" disabled>{{ $ml.get('finishedReceiptChoose') }}</option>
                            <option v-for="item in materials" :key="item.id" :value="item.id">{{ item.name }}{{ item.code ? ' (' + item.code + ')' : '' }}</option>
                        </select>
                    </f7-list-item>
                    <f7-list-item v-if="loadingContainers" key="packing-loading" :title="$ml.get('packing') + ' (kg)'" aria-busy="true">
                        <span slot="after" class="dropdown-loading" role="status" aria-live="polite">
                            <f7-preloader :size="18" aria-hidden="true" />
                            <span>{{ $ml.get('finishedReceiptLoadingOptions') }}</span>
                        </span>
                    </f7-list-item>
                    <f7-list-item v-else :key="'packing-' + containerVersion" :title="$ml.get('packing') + ' (kg)'" smart-select :smart-select-params="selectParams" :disabled="saving || !form.material_id || !packings.length">
                        <select v-model="form.packing_id" name="packing_id">
                            <option value="" disabled>{{ $ml.get('finishedReceiptChoose') }}</option>
                            <option v-for="item in packings" :key="item.id" :value="item.id">{{ item.name }}</option>
                        </select>
                    </f7-list-item>
                    <f7-list-input type="number" min="0" step="any" :label="$ml.get('finishedReceiptAmount')" :value="form.amount" :disabled="saving" @input="form.amount = $event.target.value" />
                    <f7-list-item v-if="loadingContainers" key="container-loading" :title="$ml.get('finishedReceiptContainer')" aria-busy="true">
                        <span slot="after" class="dropdown-loading" role="status" aria-live="polite">
                            <f7-preloader :size="18" aria-hidden="true" />
                            <span>{{ $ml.get('finishedReceiptLoadingOptions') }}</span>
                        </span>
                    </f7-list-item>
                    <f7-list-item v-else :key="'containers-' + containerVersion" :title="$ml.get('finishedReceiptContainer')" smart-select :smart-select-params="selectParams" :disabled="saving || !form.material_id">
                        <select v-model="form.container_id" name="container_id">
                            <option value="" disabled>{{ $ml.get('finishedReceiptChoose') }}</option>
                            <optgroup v-for="group in containers" :key="group.id" :label="group.name">
                                <option v-for="item in group.containers" :key="item.id" :value="item.id">{{ item.inventory ? item.inventory.code + ': ' : '' }}{{ item.name }} ({{ $ml.get('space') }}: {{ format(item.max_capacity - item.capacity) }} kg)</option>
                            </optgroup>
                        </select>
                    </f7-list-item>
                </f7-list>
                <f7-block v-if="form.material_id && !loadingContainers && !loadFailed && !packings.length">{{ $ml.get('finishedReceiptNoPackings') }}</f7-block>
                <f7-block v-if="form.material_id && !loadingContainers && !hasContainers">{{ $ml.get('finishedReceiptNoContainers') }}</f7-block>
                <f7-block class="receipt-summary">
                    <div>{{ $ml.get('finishedReceiptWeight') }}: <strong>{{ format(amount) }} kg</strong></div>
                    <div v-if="selectedPacking && validAmount" class="receipt-conversion">{{ $ml.get('finishedReceiptEquivalent') }}: {{ format(amount) }} kg ÷ {{ format(selectedPacking.value) }} kg ≈ {{ format(bagQuantity) }} {{ $ml.get('finishedReceiptBags') }}</div>
                    <div v-if="selectedContainer">{{ $ml.get('finishedReceiptStockAfter') }}: {{ format(Number(selectedContainer.capacity) + amount) }} kg</div>
                    <div v-if="overCapacity" class="text-color-red">{{ $ml.get('finishedReceiptOverCapacity') }}</div>
                </f7-block>
                <f7-block>
                    <f7-button fill round :disabled="!canSave" @click="save">{{ $ml.get(saving ? 'processing' : 'finishedReceiptSave') }}</f7-button>
                </f7-block>
                <f7-block v-if="errorMessage" class="text-color-red">{{ errorMessage }}</f7-block>
                <f7-block v-if="loadFailed"><f7-button @click="load">{{ $ml.get('finishedReceiptRetry') }}</f7-button></f7-block>
                <f7-block-title>{{ $ml.get('finishedReceiptRecent') }}</f7-block-title>
                <f7-list media-list>
                    <f7-list-item v-for="receipt in receipts" :key="receipt.id" :title="receipt.code" :subtitle="receipt.material ? receipt.material.name : ''" :after="format(receipt.amount) + ' kg'">
                        <div slot="text">
                            {{ receipt.packing_name }} · {{ $ml.get('finishedReceiptEquivalent') }} ≈ {{ format(receipt.quantity) }} {{ $ml.get('finishedReceiptBags') }}<br>
                            {{ receipt.container ? receipt.container.name : '' }} · {{ receipt.created_at }}
                        </div>
                    </f7-list-item>
                </f7-list>
                <f7-block v-if="!loading && !receipts.length">{{ $ml.get('finishedReceiptEmpty') }}</f7-block>
            </template>
        </f7-page>
    </f7-view>
</template>

<script>
import Query from '@/services/api/Query'
import Mutation from '@/services/api/Mutation'
import CMNavbar from './Navbar'

const emptyForm = () => ({ material_id: '', packing_id: '', amount: '', container_id: '' })

export default {
    components: { 'cm-navbar': CMNavbar },
    data() {
        return {
            form: emptyForm(), formVersion: 0, containerVersion: 0,
            materials: [], packings: [], containers: [], receipts: [],
            loading: false, loadingContainers: false, saving: false,
            loadFailed: false, errorMessage: '', containerRequest: 0,
            attempt: null,
            selectParams: { openIn: 'page', closeOnSelect: true, searchbar: true },
        }
    },
    computed: {
        canCreate() { return this.$helper.hasPermission('finished_receipt_management') },
        selectedPacking() { return this.packings.find(item => Number(item.id) === Number(this.form.packing_id)) },
        selectedContainer() {
            for (const group of this.containers) {
                const found = group.containers.find(item => Number(item.id) === Number(this.form.container_id))
                if (found) return found
            }
            return null
        },
        validAmount() { return Number.isFinite(Number(this.form.amount)) && Number(this.form.amount) > 0 },
        amount() { return this.validAmount ? Number(this.form.amount) : 0 },
        bagQuantity() { return this.selectedPacking ? this.amount / Number(this.selectedPacking.value) : 0 },
        hasContainers() { return this.containers.some(group => group.containers.length > 0) },
        overCapacity() { return this.selectedContainer && this.amount > Number(this.selectedContainer.max_capacity) - Number(this.selectedContainer.capacity) },
        canSave() {
            return this.canCreate && !this.loading && !this.loadFailed && !this.loadingContainers && !this.saving && this.form.material_id && this.selectedPacking && this.selectedContainer && this.amount > 0 && Number.isFinite(this.amount) && !this.overCapacity
        },
    },
    methods: {
        format(value) { return Number(value || 0).toLocaleString(this.$ml.current === 'English' ? 'en-US' : 'vi-VN', { maximumFractionDigits: 6 }) },
        read(result, field) {
            if (result.data.errors) throw new Error(this.$helper.errors(result.data.errors))
            return result.data.data[field]
        },
        async load() {
            if (!this.canCreate || this.loading) return
            this.loading = true
            this.errorMessage = ''
            this.loadFailed = false
            try {
                const result = await Query.withFinishedReceiptOptions().withFinishedReceipts().get()
                const options = this.read(result, 'finished_receipt_options')
                this.materials = options.materials
                this.packings = options.packings
                this.receipts = this.read(result, 'finished_receipts')
                if (this.form.material_id) await this.loadContainers(this.form.material_id)
            } catch (error) {
                this.errorMessage = error.message || this.$ml.get('finishedReceiptFailed')
                this.loadFailed = true
            } finally { this.loading = false }
        },
        async loadContainers(materialId) {
            const request = ++this.containerRequest
            this.form.container_id = ''
            this.form.packing_id = ''
            this.packings = []
            this.containers = []
            this.containerVersion++
            this.loadingContainers = !!materialId
            if (!materialId) return
            this.loadFailed = false
            this.errorMessage = ''
            try {
                const result = await Query.withFinishedReceiptOptions(materialId).get()
                if (request !== this.containerRequest) return
                const options = this.read(result, 'finished_receipt_options')
                this.packings = options.packings
                if (this.packings.length === 1) this.form.packing_id = this.packings[0].id
                this.containers = [{ id: 'available', name: this.$ml.get('finishedReceiptContainer'), containers: options.containers }]
                this.containerVersion++
            } catch (error) {
                if (request === this.containerRequest) {
                    this.errorMessage = error.message || this.$ml.get('finishedReceiptFailed')
                    this.loadFailed = true
                }
            } finally {
                if (request === this.containerRequest) this.loadingContainers = false
            }
        },
        async save() {
            if (!this.canSave) return
            this.saving = true
            this.errorMessage = ''
            const payload = JSON.stringify(this.form)
            if (!this.attempt || this.attempt.payload !== payload) {
                this.attempt = { payload, key: Date.now().toString(36) + '-' + Math.random().toString(36).slice(2) + '-' + Math.random().toString(36).slice(2) }
            }
            try {
                const result = await Mutation.createFinishedReceipt({ ...this.form, request_key: this.attempt.key }).execute()
                const receipt = this.read(result, 'create_finished_receipt')
                this.form = emptyForm()
                this.formVersion++
                this.attempt = null
                this.$f7.dialog.alert(this.$ml.get('finishedReceiptSaved') + ': ' + receipt.code, this.$ml.get('information'))
                await this.load()
            } catch (error) {
                this.errorMessage = error.message || this.$ml.get('finishedReceiptFailed')
            } finally { this.saving = false }
        },
    },
    watch: {
        'form.material_id'(value) { this.loadContainers(value) },
    },
    created() { this.load() },
}
</script>

<style scoped>
.receipt-summary { line-height: 1.8; }
.receipt-conversion { color: #666; }
.dropdown-loading { display: inline-flex; align-items: center; gap: 8px; font-size: 14px; }
.dropdown-loading .preloader { flex-shrink: 0; }
</style>
